import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';
import { Registration } from '@stax-core/types-omni';
import { RegistrationForm } from '../../@types';
import { coreapi } from '../../api';
import {
  AuthStore,
  RegistrationStore,
  updateRegistration,
} from '../../context';
import AddressAutocomplete from '../../components/AddressAutocomplete/AddressAutocomplete';
import { useToaster, useWriteableFieldFormValidation } from '../../hooks';
import {
  handleAddressAutocompleteSelect,
  processAddressComponents,
} from '../../util/addressAutocomplete.util';
import { BackButton } from '../components/BackButton';
import { NextButton } from '../components/NextButton';
import { CenteredContent } from '../components/CenteredContent';
import { Map } from '../components/Map';
import { GeocodeResult, getGeocode, getLatLng } from 'use-places-autocomplete';
import { MainForm } from '../components/application/MainForm';

interface FieldValues
  extends Pick<
    Registration,
    | 'owner_address_1'
    | 'owner_address_city'
    | 'owner_address_state'
    | 'owner_address_zip'
    | 'owner_address_country'
  > {}

const AddressField = styled.div`
  margin: 10px 0;
`;

export const PersonalAddressPage: FC<RegistrationForm<FieldValues>> = () => {
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const {
    state: { auth },
  } = useContext(AuthStore);
  const { toaster, toast } = useToaster();
  const authToken = auth?.token ?? null;

  // lat: 36 and lng: -96 are coordinates of the United States
  const [latLng, setLatLng] = useState({
    lat: 36,
    lng: -96,
  });
  const [zoom, setZoom] = useState(3);

  //todo render map centered on persisted address on page load
  useEffect(() => {
    const fetchCoords = async () => {
      if (registration?.owner_address_1) {
        const address = `${registration.owner_address_1}, ${registration.owner_address_city}, ${registration.owner_address_state}, ${registration.owner_address_zip}`;
        const [geoCode] = await getGeocode({ address });
        const { lat, lng } = await getLatLng(geoCode);
        setLatLng({ lat, lng });
        setZoom(15);
      }
    };
    fetchCoords();
  }, [
    registration?.owner_address_1,
    registration?.owner_address_city,
    registration?.owner_address_state,
    registration?.owner_address_zip,
  ]);

  const onSelectAddress = async (geoCode: GeocodeResult) => {
    try {
      const { lat, lng } = await getLatLng(geoCode);
      setLatLng({ lat, lng });
      setZoom(15);
    } catch (e) {
      // do nothing
    }
  };

  const onSubmit = useCallback(
    async (formValues: FieldValues) => {
      const payload: Partial<Registration> = {
        owner_address_1: formValues.owner_address_1 ?? '',
        owner_address_city: formValues.owner_address_city ?? '',
        owner_address_state: formValues.owner_address_state ?? '',
        owner_address_zip: formValues.owner_address_zip ?? '',
        owner_address_country: formValues.owner_address_country ?? '',
      };

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));
        return true;
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toaster, toast],
  );

  const validate = useWriteableFieldFormValidation((values: FieldValues) => {
    const errors: Partial<Record<keyof FieldValues, any>> = {};

    const requiredFields = [
      'owner_address_1',
      'owner_address_city',
      'owner_address_state',
      'owner_address_zip',
      'owner_address_country',
    ];

    requiredFields.forEach(field => {
      if (!values[field]) {
        errors[field] = 'Required';
      }
    });

    return errors;
  });

  return (
    <MainForm<FieldValues>
      initialValues={mapInitialFormValues(registration)}
      validate={validate}
      onSubmit={onSubmit}
    >
      {formProps => (
        <CenteredContent
          id="personal-address"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <BackButton id="personal-address-back" />
          <h2>Home address</h2>
          <div>
            <p>We need your home address to open your account.</p>
            <AddressField>
              <AddressAutocomplete
                inputProps={{
                  id: 'address-id',
                  label: 'Search Address',
                  value: formProps?.values?.owner_address_1 ?? '',
                  icon: ['fas', 'search'],
                }}
                onSelect={(data, geoCode) => {
                  onSelectAddress(geoCode);
                  handleAddressAutocompleteSelect(
                    data,
                    {
                      streetAddress: 'owner_address_1',
                      city: 'owner_address_city',
                      state: 'owner_address_state',
                      zip: 'owner_address_zip',
                      country: 'owner_address_country',
                    },
                    formProps.form.mutators.setFieldValue,
                  );
                }}
              />
            </AddressField>

            <Map
              lat={latLng.lat}
              lng={latLng.lng}
              zoom={zoom}
              onClick={async coords => {
                setLatLng(coords);
                const [geoCode] = await getGeocode({ location: coords });
                const data = processAddressComponents(
                  geoCode.address_components,
                );
                handleAddressAutocompleteSelect(
                  data,
                  {
                    streetAddress: 'owner_address_1',
                    city: 'owner_address_city',
                    state: 'owner_address_state',
                    zip: 'owner_address_zip',
                    country: 'owner_address_country',
                  },
                  formProps.form.mutators.setFieldValue,
                );
              }}
            />
          </div>
          <NextButton id="personal-address-continue" formProps={formProps} />
        </CenteredContent>
      )}
    </MainForm>
  );
};

function mapInitialFormValues(
  registration: Registration | null,
): Partial<FieldValues> {
  return registration
    ? {
        owner_address_1: registration.owner_address_1,
        owner_address_city: registration.owner_address_city,
        owner_address_state: registration.owner_address_state,
        owner_address_zip: registration.owner_address_zip,
        owner_address_country: registration.owner_address_country,
      }
    : ({} as any);
}
